import type { ThemeSummary } from '@icetus/theme-kit';
import type {
  AuditListView,
  ArtAspect,
  InvitationPreview,
  MembersView,
  BillingView,
  BrandArtAvailability,
  BrandArtJob,
  BrandArtMode,
  BrandArtStyle,
  BrandImageConfigView,
  BrandImageTestResult,
  CoverArtStyle,
  CategoryInput,
  CategoryNode,
  ConfirmUploadInput,
  CreateOrderInput,
  DomainView,
  DowngradeChoices,
  DowngradePreview,
  MediaItem,
  OnboardingPublishResult,
  OnboardingView,
  OrderView,
  PresignInput,
  PresignResult,
  PlanCode,
  PreviewDraft,
  PreviewToken,
  PageDetail,
  PageSeo,
  PageStatus,
  PageSummary,
  Lead,
  LeadResource,
  LeadStatus,
  PlatformOverview,
  ProductDetail,
  ProductInput,
  ProductListResult,
  ProductStatus,
  ProvisioningView,
  ReorderCategoriesInput,
  SiteAgentRunView,
  SiteContext,
  SiteNav,
  SiteSettings,
  SiteShell,
  SiteShellBlock,
  SiteShellPatch,
  ThemeMarketView,
  ThemeOrderView,
  ThemePackageView,
  ThemePreviewTicket,
  ThemePublishResult,
  ThemeSubscriptionView,
  ThemeValidationView,
  StartSiteAgentInput,
  StartSiteAgentResult,
  StoredBlock,
  SuggestBrandInput,
  SuggestBrandResult,
  TopUpSiteAgentArtResult,
  UpdateOnboardingInput,
  UpdateSiteSettingsInput,
  TranslationEstimate,
  TranslationGlossaryEntry,
  InlineTranslationInput,
  InlineTranslationResult,
  TranslationJobSummary,
  TranslationOverview,
  TranslationProviderView,
  TranslationScope,
  TranslationUnitView,
  UpdateBrandImageConfigInput,
  UpdateTranslationProviderInput,
} from '@icetus/contracts';

/**
 * 控制台 API 客户端。
 *
 * 认证方式变了：不再把管理员令牌存进 localStorage，而是**登录后由服务端下发
 * httpOnly cookie**。这个改动不是为了好看——localStorage 里的令牌对任何一段
 * 注入的脚本都可读，而 httpOnly 的 cookie 读不到。XSS 从"直接拿走后台权限"
 * 降级成"只能以受害者身份操作当前页面"。
 *
 * 请求仍然同域：控制台由 nginx 托管在 console.icetus.net，`/v1/` 反代到平台
 * API。没有 CORS，也不需要把 API 地址编进构建产物。
 */

const API_BASE = import.meta.env.VITE_API_BASE ?? '';

export type Role = 'owner' | 'admin' | 'editor' | 'viewer';

export interface Membership {
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  role: Role;
  /**
   * 这一条不是真实成员关系，而是平台超管"能看到全部租户"拼出来的。
   *
   * 只影响界面怎么标（显示"超管视角"而不是角色名），不影响任何请求：
   * 请求仍然只带 `x-tenant-id`，由服务端判定。
   */
  viaPlatformAdmin?: boolean;
}

export interface MeResponse {
  user: {
    id: string;
    email: string;
    emailVerified: boolean;
    /** 显示名。未填时为 null，界面用邮箱前缀顶上 */
    displayName: string | null;
    /** 登记的手机号（已归一化）。未验证 */
    phone: string | null;
    /** 平台超管：决定"发布主题"入口是否出现。真正的权限在后端再查一次 */
    platformAdmin: boolean;
  } | null;
  memberships: Membership[];
  /** 以运维令牌访问时为 true（此时 user 为 null、memberships 为空） */
  adminToken: boolean;
}

export interface QuotaUsage {
  storageUsedMb: number;
  storageLimitMb: number;
  productsUsed: number;
  productsLimit: number;
}

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    /**
     * 后端附带的上下文（`host`、`status` 等）。
     * 不能丢：客服排查时靠它对齐后端日志里的同一条记录，
     * 前端只显示 message 也仍然需要它做遥测与提示分级。
     */
    readonly details: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * 单次请求的超时。
 *
 * 20 秒是"比服务端慢很多、但比浏览器默认无限等待短"的值：控制面接口正常在
 * 几十毫秒内返回，20 秒还没回来说明这条连接已经死了（长连接被中间设备掐断、
 * 或者跨境链路在丢包），继续等下去只会让客户盯着一个空页面。
 */
const REQUEST_TIMEOUT_MS = 20_000;

/**
 * 建站这条请求单独放宽。
 *
 * 服务端事务本身 40ms 就返回，慢的是链路：客户经 Cloudflare 回源，
 * 实测 4~25 秒的抖动并不罕见（2026-11-04 有客户就这样卡住）。
 * 用默认的 20 秒会让**其实已经建好**的请求在浏览器侧先超时，
 * 客户再点一次就吃 409 `site_limit` —— 他会以为整件事彻底坏了。
 *
 * 等久一点，比误报失败便宜。真正超时了还有 `findCreatedSite` 兜底。
 */
const CREATE_SITE_TIMEOUT_MS = 60_000;

/** 网络层失败后重试一次前的间隔。首次失败的几乎都是死连接，换个新连接就通了 */
const NETWORK_RETRY_DELAY_MS = 400;

/**
 * 这是不是"根本没拿到响应"，而不是"拿到了 4xx/5xx"。
 *
 * 前者是 `fetch` 自己抛的 TypeError（浏览器里显示成 `Failed to fetch`），
 * 后者在下面被转成带状态码的 ApiError —— 两者必须分开：网络失败可以重试，
 * 401/403 重试一百次也还是没权限。
 */
function isNetworkFailure(err: unknown): boolean {
  if (err instanceof DOMException && (err.name === 'TimeoutError' || err.name === 'AbortError')) {
    return true;
  }
  return err instanceof TypeError;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function requestOnce<T>(
  path: string,
  init: RequestInit,
  tenantId: string | undefined,
  timeoutMs: number,
): Promise<T> {
  const headers: Record<string, string> = {};
  if (tenantId) headers['x-tenant-id'] = tenantId;
  if (init.body) headers['content-type'] = 'application/json';

  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
    // 显式声明：会话在 cookie 里，跨域请求不该带上它
    credentials: 'same-origin',
    signal: AbortSignal.timeout(timeoutMs),
  });

  if (!res.ok) {
    let code = 'http_error';
    let message = `请求失败（${res.status}）`;
    let details: Record<string, unknown> = {};
    try {
      const body = (await res.json()) as Record<string, unknown>;
      if (typeof body['code'] === 'string') code = body['code'];
      if (typeof body['message'] === 'string') message = body['message'];
      const { code: _code, message: _message, ...rest } = body;
      details = rest;
    } catch {
      /* 非 JSON 响应（比如 nginx 502 页面），保留默认文案 */
    }
    throw new ApiError(code, message, res.status, details);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * 带网络重试的请求入口。
 *
 * 只重试**读**接口（GET）：写接口重试可能把一次下单变成两次。
 * 重试也不掩盖问题 —— 两次都失败会抛 `network_error`，界面显示的是
 * "网络连接失败，请检查网络后重试"，而不是浏览器原文 `Failed to fetch`。
 */
async function request<T>(
  path: string,
  init: RequestInit = {},
  tenantId?: string,
  timeoutMs: number = REQUEST_TIMEOUT_MS,
): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase();
  const attempts = method === 'GET' ? 2 : 1;
  let lastError: unknown = null;

  for (let i = 0; i < attempts; i += 1) {
    try {
      return await requestOnce<T>(path, init, tenantId, timeoutMs);
    } catch (err) {
      if (!isNetworkFailure(err)) throw err;
      lastError = err;
      if (i + 1 < attempts) await sleep(NETWORK_RETRY_DELAY_MS);
    }
  }

  throw new ApiError(
    'network_error',
    '网络连接失败：请求没能到达服务器。请检查网络后重试；如果一直这样，刷新页面。',
    0,
    { cause: lastError instanceof Error ? lastError.message : String(lastError) },
  );
}

/**
 * 登录。返回值同样经过 `withPlatformTenants`。
 *
 * 登录响应里的 memberships 是**真实成员关系**，超管为 `[]`。
 * 不补齐的话，超管登录后拿到的名册是空的：租户下拉没得选、
 * `x-tenant-id` 一律为空，各页面全部取不到数据 —— 而刷新一下就正常了，
 * 因为刷新走的是 loadMe。两个接口返回同一个类型，所以这个差别
 * 在编译器那里是完全隐形的。
 *
 * 代价只有超管多一次 `GET /v1/tenants`，普通账号在这里直接返回。
 */
export function login(email: string, password: string): Promise<MeResponse> {
  return request<MeResponse>('/v1/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  }).then(withPlatformTenants);
}

export function logout(): Promise<{ ok: true }> {
  return request<{ ok: true }>('/v1/auth/logout', { method: 'POST' });
}

/** 重发验证邮件。需要已登录 */
export function resendVerification(): Promise<{ ok: true; alreadyVerified: boolean }> {
  return request<{ ok: true; alreadyVerified: boolean }>('/v1/auth/resend-verification', {
    method: 'POST',
  });
}

/**
 * 改自己的资料。
 *
 * 只传要改的字段：服务端是 PATCH 语义，没传的保持原值。这里刻意不提供
 * "先读一次再整体提交"的封装 —— 那种写法在并发下会用旧值覆盖别人的新值，
 * 而用户看到的是"我刚改的名字过一会儿自己变回去了"。
 */
export function updateProfile(input: {
  displayName?: string;
  phone?: string;
}): Promise<NonNullable<MeResponse['user']>> {
  return request<NonNullable<MeResponse['user']>>('/v1/auth/profile', {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

/**
 * 改密码。需要当前密码。
 *
 * 成功后会踢掉**其他**设备的会话，当前这条保留 —— 所以界面上不能提示
 * "请重新登录"，那会让客户以为当前页面已经失效了，而其实还能用。
 */
export function changePassword(input: {
  currentPassword: string;
  newPassword: string;
}): Promise<{ ok: true; otherSessionsCleared: true }> {
  return request<{ ok: true; otherSessionsCleared: true }>('/v1/auth/password', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** 登录设备列表里的一行。服务端已按 UA 解析好，前端不重复判断 */
export interface LoginSession {
  id: string;
  device: { browser: string; os: string; mobile: boolean; label: string };
  ip: string | null;
  createdAt: string;
  lastSeenAt: string;
  expiresAt: string;
  current: boolean;
}

export function fetchSessions(): Promise<{ sessions: LoginSession[] }> {
  return request<{ sessions: LoginSession[] }>('/v1/auth/sessions');
}

/**
 * 退掉一条登录。
 *
 * `wasCurrent` 为真说明客户把自己当前这条退掉了 —— 那样服务端已经删了会话，
 * 但 cookie 还在浏览器里。调用方必须接着走登出流程（清本地状态 + 回登录页），
 * 否则页面会停在原地，下一个请求才 401。
 */
export function revokeSession(id: string): Promise<{ ok: true; wasCurrent: boolean }> {
  return request<{ ok: true; wasCurrent: boolean }>(`/v1/auth/sessions/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

/** 退出其他所有设备。当前这条保留 */
export function revokeOtherSessions(): Promise<{ ok: true; removed: number }> {
  return request<{ ok: true; removed: number }>('/v1/auth/sessions', { method: 'DELETE' });
}

/**
 * 申请重置密码。
 *
 * 返回体与"这个邮箱是否注册过"**完全无关**（防账号枚举）。所以界面只能
 * 提示"如果这个邮箱注册过，我们已发送邮件"，不能写"已发送到你的邮箱"——
 * 后者对未注册的邮箱是假话，而且和注册页一样，会让老客户误以为操作成功了。
 */
export function forgotPassword(email: string): Promise<{ ok: true; ttlMinutes: number }> {
  return request<{ ok: true; ttlMinutes: number }>('/v1/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

/** 用令牌设置新密码。成功后服务端会清掉该账号的全部会话 */
export function resetPassword(
  token: string,
  password: string,
): Promise<{ ok: true; email: string }> {
  return request<{ ok: true; email: string }>('/v1/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ token, password }),
  });
}

/**
 * 当前身份与可访问的租户。
 *
 * 这是租户下拉的**唯一**数据源。不要再调 /v1/tenants ——
 * 那是平台视角的全量名册，普通用户调会 403（而且不该看到别人的名字）。
 */
export function fetchMe(): Promise<MeResponse> {
  return request<MeResponse>('/v1/auth/me');
}

/** 平台视角的租户名册。只有超管（或运维令牌）能读，普通账号 403 */
export function fetchAllTenants(): Promise<{ items: TenantSummary[] }> {
  return request<{ items: TenantSummary[] }>('/v1/tenants');
}

/**
 * 平台总览报表（一行一个租户的运营指标）。
 *
 * 与 `fetchAllTenants` 分开而不是给名册加参数：名册每个请求都要跑
 * （租户下拉要用），而这份查询有十几个相关子查询，一个月看几次。
 * 合并之后租户下拉会为了一张报表多等几十毫秒，没人会想到是这个原因。
 */
export function fetchPlatformOverview(): Promise<PlatformOverview> {
  return request<PlatformOverview>('/v1/tenants/overview');
}

// ── 资料库与询盘 ──────────────────────────────────────────────
//
// 资料文件走与图片相同的直传：浏览器把 PDF 直接 PUT 到对象存储，
// 我们的 API 只签票与核对。40MB 的目录走中转会把事件循环堵住几百毫秒，
// 表现为"整站突然慢了一下"，而查不到原因。

export interface ResourceTicket {
  key: string;
  uploadUrl: string;
  headers: Record<string, string>;
  expiresIn: number;
}

export function fetchResources(
  tenantId: string,
  siteId: string,
): Promise<{ items: LeadResource[] }> {
  return request<{ items: LeadResource[] }>(
    `/v1/resources?siteId=${encodeURIComponent(siteId)}`,
    {},
    tenantId,
  );
}

export function presignResource(
  tenantId: string,
  input: { siteId: string; mime: string; bytes: number },
): Promise<ResourceTicket> {
  return request<ResourceTicket>(
    '/v1/resources/presign',
    { method: 'POST', body: JSON.stringify(input) },
    tenantId,
  );
}

/**
 * 直传对象存储，并把上传进度报出来。
 *
 * 这里刻意不用 `fetch`：它读得到**下载**的进度，但请求体是一次性交出去的，
 * 浏览器至今没有 `upload` 进度事件。50MB 的产品目录用 fetch 传，客户选完文件
 * 只会看到按钮上那句不动的「处理中…」，十几秒后最常见的反应是再点一次 ——
 * 同一份文件传两遍，对象存储里留两份孤儿对象。`XMLHttpRequest` 是目前唯一
 * 暴露 `upload.onprogress` 的 API，这一跳只能用回它。
 *
 * 同样刻意不走 `request` 封装：票据里的 `content-type` / `cache-control` 已经签进
 * 签名，多带一个会话头就是 SignatureDoesNotMatch；失败也要按「文件没传上去」
 * 来讲，而不是「网络异常」。
 */
export function putToStorage(
  ticket: Pick<ResourceTicket, 'uploadUrl' | 'headers'>,
  file: File,
  onProgress?: (sent: number, total: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    // 第三个参数 true = 异步。同步 XHR 在主线程上是禁止的，而且会卡住整页
    xhr.open('PUT', ticket.uploadUrl, true);
    for (const [name, value] of Object.entries(ticket.headers)) {
      xhr.setRequestHeader(name, value);
    }
    xhr.upload.onprogress = (event) => {
      // total 为 0 表示长度不可算（浏览器没给 Content-Length 时）。这种情况
      // 宁可不报数字，也不要报一个假的 0% —— 那会让人以为停在起点
      if (event.lengthComputable) onProgress?.(event.loaded, event.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      const detail = (xhr.responseText ?? '').slice(0, 120);
      reject(new Error(`上传到对象存储失败（HTTP ${xhr.status}）${detail ? `：${detail}` : ''}`));
    };
    // 这三种失败在界面上是不同的话：断网要换网络，取消是客户自己点的，
    // 超时则通常是文件太大 + 上行太慢
    xhr.onerror = () => reject(new Error('上传被中断：网络断开或跨域被拦，换网络后重试'));
    xhr.ontimeout = () => reject(new Error('上传超时：文件可能太大或上行太慢'));
    xhr.onabort = () => reject(new Error('上传已取消'));
    // 不设 timeout：50MB 的文件在客户那头可能要几分钟，任何"保险起见"的超时
    // 都会把正常上传掐断
    xhr.send(file);
  });
}

export function createResource(
  tenantId: string,
  input: {
    siteId: string;
    key: string;
    name: string;
    fileName: string;
    bytes: number;
    mime: string;
  },
): Promise<LeadResource> {
  return request<LeadResource>(
    '/v1/resources',
    { method: 'POST', body: JSON.stringify(input) },
    tenantId,
  );
}

export function updateResource(
  tenantId: string,
  id: string,
  patch: { name?: string; enabled?: boolean },
): Promise<void> {
  return request<void>(
    `/v1/resources/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: JSON.stringify(patch) },
    tenantId,
  );
}

export function deleteResource(tenantId: string, id: string): Promise<void> {
  return request<void>(`/v1/resources/${encodeURIComponent(id)}`, { method: 'DELETE' }, tenantId);
}

export function fetchLeads(
  tenantId: string,
  query: { siteId?: string; status?: string; limit?: number; offset?: number },
): Promise<{ items: Lead[]; total: number; unread: number }> {
  const params = new URLSearchParams();
  if (query.siteId) params.set('siteId', query.siteId);
  if (query.status) params.set('status', query.status);
  if (query.limit) params.set('limit', String(query.limit));
  if (query.offset) params.set('offset', String(query.offset));
  const qs = params.toString();
  return request<{ items: Lead[]; total: number; unread: number }>(
    `/v1/leads${qs ? `?${qs}` : ''}`,
    {},
    tenantId,
  );
}

/** 侧栏红点。只为一个数字，不为它把整页数据拉下来 */
export function fetchUnreadLeads(tenantId: string): Promise<{ unread: number }> {
  return request<{ unread: number }>('/v1/leads/unread', {}, tenantId);
}

export function setLeadStatus(tenantId: string, id: string, status: LeadStatus): Promise<void> {
  return request<void>(
    `/v1/leads/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: JSON.stringify({ status }) },
    tenantId,
  );
}

export interface TenantSummary {
  id: string;
  slug: string;
  name: string;
  siteCount: number;
}

/**
 * 把"我能操作哪些租户"补齐成超管视角，拼成成员名册的形状。
 *
 * 为什么要拼：平台超管**不属于任何租户**（2026-10-30 的产品约定），
 * `me.memberships` 因此是空的 —— 而控制台整层（租户下拉、权限按钮、
 * 每个页面的 `x-tenant-id`）都以 memberships 为唯一输入。不拼的话，
 * 超管登录进去看到的是一张空控制台，只能靠手拼 curl 干活。
 *
 * 拼出来的这几条 `role: 'owner'`，与后端 `TenantGuard` 对超管的判定一致
 * （不是成员 → 按 owner 算）。**前端这份只是让按钮显示出来**，真正的
 * 放行仍在后端；两边不一致时的症状是"点了 403"，而不是越权。
 *
 * 名册拉失败不影响登录：超管仍然能进控制台，只是没有租户可选。
 * 让它把整个登录流程打挂是最糟的取舍 —— 那会让"名册接口挂了"变成
 * "谁都进不了后台"。
 *
 * **必须包住每一个返回 MeResponse 的入口，不能只在调用处记得包。**
 * `/v1/auth/login` 和 `/v1/auth/me` 返回同一个类型，形状完全一样，
 * 于是"拿一个 MeResponse 直接 apply 当作控制台的名册"看起来天经地义 ——
 * 曾经就是这么漏的：登录后直接是登录响应当名册，超管登录进去租户下拉是空的，
 * 刷新一下才正常（刷新走的是 loadMe，那条路径补齐了）。
 * 类型相同但语义不同，编译器不会提醒，所以补齐要放在出口。
 */
async function withPlatformTenants(me: MeResponse): Promise<MeResponse> {
  if (!me.user?.platformAdmin) return me;

  let items: TenantSummary[] = [];
  try {
    items = (await fetchAllTenants()).items;
  } catch (err) {
    console.warn('[console] 租户名册读取失败，超管将看不到租户列表', err);
    return me;
  }

  const known = new Set(me.memberships.map((m) => m.tenantId));
  const extra: Membership[] = items
    .filter((t) => !known.has(t.id))
    .map((t) => ({
      tenantId: t.id,
      tenantName: t.name,
      tenantSlug: t.slug,
      role: 'owner',
      viaPlatformAdmin: true,
    }));

  // 自己真实所属的租户排在前面：超管偶尔也可能就是某个租户的成员，
  // 那时该显示真实角色，而不是被"超管视角"盖成 owner
  return { ...me, memberships: [...me.memberships, ...extra] };
}

/**
 * 载入"我是谁 + 我能操作哪些租户"。刷新页面时走这条。
 *
 * 注意它和 `login()` 是两条不同的入口，但产出的东西必须**等价** ——
 * 否则就会出现"登录后和刷新后看到的不是同一个控制台"这类只在特定账号上
 * 复现的怪现象。补齐那一步两者共用 `withPlatformTenants`。
 */
export async function loadMe(): Promise<MeResponse> {
  return withPlatformTenants(await fetchMe());
}

/**
 * 自助开通进度。
 *
 * 单独一个接口而不是塞进 `/v1/auth/me`：`me` 是每个请求都会打的热点，
 * 而开通进度只在"还没有站点"时看几次。混进去等于给所有请求加一次查询。
 * 也不带 `x-tenant-id` —— 开通失败时客户可能连租户都没有。
 */
export function fetchProvisioning(): Promise<ProvisioningView> {
  return request<ProvisioningView>('/v1/provisioning/status');
}

/** 重试开通。只对失败的任务有意义，成功的会直接返回原状态 */
export function retryProvisioning(): Promise<ProvisioningView> {
  return request<ProvisioningView>('/v1/provisioning/retry', { method: 'POST' });
}

/**
 * 开通引导（P1-25）。
 *
 * 读和写都返回**完整视图**：客户保存后界面要立刻显示新地址和新的完成度。
 * 让前端自己拼清单，就得在前端复刻服务端的规则 —— 那份规则迟早会不一致，
 * 症状是"清单说还差联系方式，设置页里明明填了"。
 */
/**
 * 把站点 ID 拼成查询串。
 *
 * 只有引导这三个接口需要它：其余接口的站点 ID 都在请求体里（或者由服务端
 * 自己推导），而引导的读/改/发布必须针对同一个站，所以三处共用同一个参数名 ——
 * 名字不一致那天会出现"读的是 A 站、发布的是 B 站"，而两边都不报错。
 */
function siteQuery(siteId?: string | null): string {
  return siteId ? `?siteId=${encodeURIComponent(siteId)}` : '';
}

export function fetchOnboarding(tenantId: string, siteId?: string | null): Promise<OnboardingView> {
  return request<OnboardingView>(`/v1/onboarding${siteQuery(siteId)}`, {}, tenantId);
}

export function updateOnboarding(
  tenantId: string,
  input: UpdateOnboardingInput,
  siteId?: string | null,
): Promise<OnboardingView> {
  return request<OnboardingView>(
    `/v1/onboarding${siteQuery(siteId)}`,
    { method: 'PATCH', body: JSON.stringify(input) },
    tenantId,
  );
}

/** 发布草稿页。未验证邮箱时 403 `email_not_verified` */
export function publishOnboarding(
  tenantId: string,
  siteId?: string | null,
): Promise<OnboardingPublishResult> {
  return request<OnboardingPublishResult>(
    `/v1/onboarding/publish${siteQuery(siteId)}`,
    { method: 'POST' },
    tenantId,
  );
}

/**
 * 站点列表 + 站点用量。
 *
 * `usage.limit = 0` 表示不限量（套餐里没配），**不是**"一个都不能建"。
 * 前端判空只看 `limit > 0`，两个地方各判一次就会出现"明明不限量却禁用了按钮"。
 */
export interface SiteListResult {
  items: SiteContext[];
  usage: { used: number; limit: number };
}

export async function fetchSiteList(tenantId: string): Promise<SiteListResult> {
  // 租户走请求头，不再拼进 URL：拼 URL 等于把"读任意租户"做成了接口
  return request<SiteListResult>('/v1/sites', {}, tenantId);
}

export async function fetchSites(tenantId: string): Promise<SiteContext[]> {
  return (await fetchSiteList(tenantId)).items;
}

/**
 * 新建站点（企业版多站点）。
 *
 * 名称只做 trim，长度校验留在服务端：前端也拦一道会出现两套上限，
 * 改一处忘一处就变成"界面说 50 字、接口说 60 字"。
 */
export function createSite(
  tenantId: string,
  name: string,
  /**
   * 建站向导一次性带上版式、语言与对外站名。
   *
   * 不传时与从前一致：极光 + 租户默认语言。
   */
  extra?: { theme?: string; locales?: string[]; title?: string; defaultLocale?: string },
): Promise<{ siteId: string; host: string }> {
  /**
   * 空数组不传。
   *
   * 服务端的 `locales` 缺省是"租户默认语言"，而 `[]` 是"一个语言都不要"——
   * 后者会被当成非法入参拒掉（一个都没有的站打不开）。与其让前端学到
   * 这个区别，不如在出口处就把它归一成"没说"。
   */
  const body: Record<string, unknown> = { name: name.trim() };
  if (extra?.theme) body['theme'] = extra.theme;
  if (extra?.locales && extra.locales.length > 0) body['locales'] = extra.locales;
  /**
   * 对外站名同样只在不空时传。
   *
   * 它决定首页首屏大标题与页面 `<title>` 的初值 —— 传内部名进去，
   * 客户建完站打开首页，第一句看到的就是"北美站"。
   */
  if (extra?.title?.trim()) body['title'] = extra.title.trim();
  /**
   * 主语言。它决定页面种在哪个语言下、`/` 落到哪、以及起始文案的中英版本。
   *
   * 建完再改就晚了：页面是 `(site_id, locale, path)`，换主语言等于要重种整站。
   */
  if (extra?.defaultLocale) body['defaultLocale'] = extra.defaultLocale;
  return request('/v1/sites', { method: 'POST', body: JSON.stringify(body) }, tenantId, CREATE_SITE_TIMEOUT_MS);
}

/** 恢复被降档收缩暂停的站点。不占新名额：暂停的本来就计入已用 */
export function resumeSite(tenantId: string, siteId: string): Promise<{ ok: true }> {
  return request(`/v1/sites/${siteId}/resume`, { method: 'POST' }, tenantId);
}

/**
 * 归档站点：前台 404，但**不再占套餐的站位数**。
 *
 * 它是降档流程的出口 —— 被暂停的站仍占名额，客户不归档就既恢复不了
 * 也建不了新站。数据一张不删，随时能恢复（`resumeSite`）。
 */
export function archiveSite(tenantId: string, siteId: string): Promise<{ ok: true }> {
  return request(`/v1/sites/${siteId}/archive`, { method: 'POST' }, tenantId);
}

/**
 * AI 建站。
 *
 * 三个接口对应三屏：问名字（其实是对话里的一轮）、发起、轮询。
 * **没有 `/plan`** —— 中间没有可编辑的蓝图，客户点了就是同意。
 */

/**
 * 起一个品牌名。
 *
 * 单独一条而不是并进 `/runs`：那会把一次建站变成两次往返加一次状态等待，
 * 而用户此刻只想要个名字。无状态、不落库；用户接受了，值随 `/runs` 一起走。
 *
 * 超时给到 30 秒：它背后是一次小模型的调用（200 token，通常 1–3 秒），
 * 但冷启动或上游抖动时 5 秒会误判成失败 —— 而失败会让客户以为名字功能坏了。
 */
export function suggestSiteAgentBrand(
  tenantId: string,
  input: SuggestBrandInput,
): Promise<SuggestBrandResult> {
  return request(
    '/v1/site-agent/brand-suggest',
    { method: 'POST', body: JSON.stringify(input) },
    tenantId,
    30_000,
  );
}

/**
 * 发起一次建站。
 *
 * 服务端**只落一行 queued 就返回**（202），建站与出图都在后台推进 ——
 * 建站加文案要十几秒，塞进请求里客户会盯着一个没反应的按钮。
 */
export function startSiteAgentRun(
  tenantId: string,
  input: StartSiteAgentInput,
): Promise<StartSiteAgentResult> {
  return request(
    '/v1/site-agent/runs',
    { method: 'POST', body: JSON.stringify(input) },
    tenantId,
    30_000,
  );
}

/** 进度。前端每两秒问一次，直到 done / failed */
export function fetchSiteAgentRun(tenantId: string, runId: string): Promise<SiteAgentRunView> {
  return request(`/v1/site-agent/runs/${runId}`, {}, tenantId);
}

/**
 * 这个租户正在跑的那一次（没有就 null）。
 *
 * 客户关掉页面再进来靠它回到进度页 —— 没有这条，那个运行就只剩下一个
 * 他自己也记不住的 id。
 */
export function fetchActiveSiteAgentRun(tenantId: string): Promise<SiteAgentRunView | null> {
  return request('/v1/site-agent/runs/active', {}, tenantId);
}

/**
 * 补一张没生成的图。
 *
 * 返回整份运行视图 —— 点完立刻替换本地状态，那一格马上从"未生成"变成转圈，
 * 不用等下一跳轮询。超时给 15 秒：它只是一次入队（删一行、插一行），
 * 慢就说明服务端有问题，不值得让客户盯着按钮。
 */
export function retrySiteAgentArt(
  tenantId: string,
  runId: string,
  slot: string,
): Promise<SiteAgentRunView> {
  return request(
    `/v1/site-agent/runs/${runId}/art/retry`,
    { method: 'POST', body: JSON.stringify({ slot }) },
    tenantId,
    15_000,
  );
}

/**
 * 补齐页面上空着、但这一轮没排过队的图位。
 *
 * 与「补一张」分开：那条按槽位名重画失败的那张，这条是让服务端重新扫一遍
 * 站点，把新出现的空图位排上（主题改版给已有区块加了图之后，老站就是这种情况）。
 */
export function topUpSiteAgentArt(
  tenantId: string,
  runId: string,
): Promise<TopUpSiteAgentArtResult> {
  return request(
    `/v1/site-agent/runs/${runId}/art/topup`,
    { method: 'POST' },
    tenantId,
    30_000,
  );
}

export async function fetchDomains(tenantId: string): Promise<DomainView[]> {
  const body = await request<{ items: DomainView[] }>('/v1/domains', {}, tenantId);
  return body.items;
}

export function createDomain(tenantId: string, siteId: string, host: string): Promise<DomainView> {
  return request<DomainView>(
    '/v1/domains',
    { method: 'POST', body: JSON.stringify({ siteId, host }) },
    tenantId,
  );
}

export function verifyDomain(tenantId: string, id: string): Promise<DomainView> {
  return request<DomainView>(`/v1/domains/${id}/verify`, { method: 'POST' }, tenantId);
}

export function getDomain(tenantId: string, id: string): Promise<DomainView> {
  return request<DomainView>(`/v1/domains/${id}`, {}, tenantId);
}

export function removeDomain(tenantId: string, id: string): Promise<DomainView> {
  return request<DomainView>(`/v1/domains/${id}`, { method: 'DELETE' }, tenantId);
}

// ── 内容 ──────────────────────────────────────────────────────────────

export async function fetchPages(tenantId: string, siteId: string): Promise<PageSummary[]> {
  const body = await request<{ items: PageSummary[] }>(
    `/v1/pages?siteId=${encodeURIComponent(siteId)}`,
    {},
    tenantId,
  );
  return body.items;
}

export function fetchPage(tenantId: string, id: string): Promise<PageDetail> {
  return request<PageDetail>(`/v1/pages/${id}`, {}, tenantId);
}

/**
 * 区块库。
 *
 * `defaults` 是服务端下发的初始属性，控制台**不要**自己拼一份默认值：
 * 默认属性、默认版本、schema 三者必须同源，前端硬编码就等于埋了一个
 * "协议升级了但前端没改"的定时炸弹，表现是客户加上去的块内容空白。
 */
/**
 * 区块表单的字段描述符。
 *
 * 与 `packages/blocks/src/protocol.ts` 里的定义是同一份东西，这里**刻意复制**而不是
 * 直接依赖 @icetus/blocks：那个包依赖 zod，把它拉进控制台的浏览器产物等于
 * 为了几个类型多带 13KB，而且它和 contracts 一样是 CJS 产物，
 * dev 侧还要再踩一次"依赖预打包"的坑。
 *
 * 代价是类型级的重复，靠服务端下发保证运行时形状一致 ——
 * 描述符是数据，不是行为，写错字段名最坏结果是某个字段不显示，
 * 不会造成数据损坏（保存时仍有 schema 兜底）。
 */
export type BlockFieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'boolean'
  | 'select'
  | 'image'
  | 'stringList'
  | 'list'
  /** 选一份「资料」（客户上传的 PDF/压缩包，见控制台「资料库」），值是资料 uuid */
  | 'resource';

export interface BlockField {
  type: Exclude<BlockFieldType, 'list' | 'stringList'>;
  key: string;
  label: string;
  hint?: string;
  nullable?: boolean;
  maxLength?: number;
  min?: number;
  max?: number;
  step?: number;
  /**
   * 空值时输入框里的灰字（服务端描述符下发）。
   *
   * 用于「留空就用默认文案」的字段：写的就是前台此刻显示的那一句。
   * 与 `BlockStringListField` 上的同名属性不是一回事（那里是每条的示例）。
   */
  placeholder?: string;
  options?: Array<{ value: string; label: string }>;
  /** 选项由控制台运行时提供（'category' = 本站在用的分类列表；'navLocation' = 主题声明的菜单分组） */
  optionsSource?: 'category' | 'navLocation';
  /** select 的值在 props 里是数字（如 columns: 2 | 3 | 4） */
  numeric?: boolean;
  /**
   * 图片字段要的形状，用于 AI 生图的画布比例。
   *
   * 有这个字段的图片位置才出现「用 AI 生成」—— 真 logo、真工厂照那类
   * 要证据的地方故意不写（真源在 `packages/blocks/src/protocol.ts`）。
   */
  artAspect?: ArtAspect;
}

export interface BlockStringListField {
  type: 'stringList';
  key: string;
  label: string;
  /** 每一条的可选值由控制台运行时提供（'category' = 本站在用的分类） */
  optionsSource?: 'category';
  hint?: string;
  max?: number;
  placeholder?: string;
}

export interface BlockListField {
  type: 'list';
  key: string;
  label: string;
  hint?: string;
  max?: number;
  itemLabel: string;
  defaultItem: Record<string, unknown>;
  titleKey?: string;
  /**
   * 条目字段。**可以是任意字段**，包括再一层 `list` / `stringList`。
   *
   * 这里曾经写成 `BlockField[]`（只有标量），而协议侧（`packages/blocks/src/protocol.ts`）
   * 一直是 `AnyBlockField[]`。类型对不上的后果不是编译失败，而是实现照着窄的那份写：
   * 条目内层统一用标量控件渲染，`tabs[].bullets` 这种嵌在条目里的字符串数组
   * 变成一个普通文本框，客户一敲就把它写成了字符串，整块随即过不了 schema，
   * 渲染时被降级成主题自带的英文演示文案。两份定义必须一致。
   */
  fields: AnyBlockField[];
}

export type AnyBlockField = BlockField | BlockStringListField | BlockListField;

export interface BlockLibraryItem {
  type: string;
  version: number;
  category: 'hero' | 'content' | 'commerce' | 'trust' | 'contact';
  title: { zh: string; en: string };
  defaults: Record<string, unknown>;
  maxPerPage?: number;
  /** 表单字段描述。为空时控制台退回通用渲染 */
  fields?: AnyBlockField[];
}

export function fetchBlocks(tenantId: string): Promise<{ items: BlockLibraryItem[] }> {
  return request<{ items: BlockLibraryItem[] }>('/v1/blocks', {}, tenantId);
}

export function saveBlocks(
  tenantId: string,
  id: string,
  blocks: StoredBlock[],
): Promise<PageDetail> {
  return request<PageDetail>(
    `/v1/pages/${id}/blocks`,
    { method: 'PUT', body: JSON.stringify({ blocks }) },
    tenantId,
  );
}

/**
 * 预览令牌（P1-17）。
 *
 * 只取令牌不取 URL：预览地址要把令牌拼到**客户站点自己的域名**上，
 * 而站点域名控制台这边已经有了（sites[].site.primaryHost）。
 */
export function createPreviewToken(tenantId: string, pageId: string): Promise<PreviewToken> {
  return request<PreviewToken>(`/v1/pages/${pageId}/preview-token`, { method: 'POST' }, tenantId);
}

/**
 * 存一份预览草稿（P1-17 实时联动）。
 *
 * 预览原先只认**已保存**内容，所以勾一个「显示」、改一句文案，右侧都不动；
 * 而保存是直接上线的，不能为了看一眼就保存。草稿把这两件事分开：
 * 存草稿只影响预览，访客看到的还是已保存的版本。
 */
export function savePreviewDraft(
  tenantId: string,
  pageId: string,
  blocks: StoredBlock[],
): Promise<PreviewDraft> {
  return request<PreviewDraft>(
    `/v1/pages/${pageId}/preview-draft`,
    { method: 'PUT', body: JSON.stringify({ blocks }) },
    tenantId,
  );
}

/** 丢掉草稿：正式保存成功后、或预览在无改动状态下打开时调用 */
export function discardPreviewDraft(tenantId: string, pageId: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/v1/pages/${pageId}/preview-draft`, { method: 'DELETE' }, tenantId);
}

export function updatePage(
  tenantId: string,
  id: string,
  patch: { title?: string | null; status?: PageStatus; seo?: Partial<PageSeo> },
): Promise<PageDetail> {
  return request<PageDetail>(
    `/v1/pages/${id}`,
    { method: 'PATCH', body: JSON.stringify(patch) },
    tenantId,
  );
}

/**
 * 新建自建页面。服务端会**一次把已开通语言都建出来**，返回的就是这批页面。
 *
 * `copyFromPageId` 给了就复制那一页的区块，没给就用主题的内容页版式做骨架。
 */
export async function createPages(
  tenantId: string,
  input: { siteId: string; title: string; path: string; copyFromPageId?: string },
): Promise<PageSummary[]> {
  const body = await request<{ items: PageSummary[] }>(
    '/v1/pages',
    { method: 'POST', body: JSON.stringify(input) },
    tenantId,
  );
  return body.items;
}

export function deletePage(tenantId: string, id: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/v1/pages/${id}`, { method: 'DELETE' }, tenantId);
}

export function fetchSiteSettings(tenantId: string, siteId: string): Promise<SiteSettings> {
  return request<SiteSettings>(`/v1/sites/${siteId}/settings`, {}, tenantId);
}

export function saveSiteSettings(
  tenantId: string,
  siteId: string,
  patch: UpdateSiteSettingsInput,
): Promise<SiteSettings> {
  return request<SiteSettings>(
    `/v1/sites/${siteId}/settings`,
    { method: 'PATCH', body: JSON.stringify(patch) },
    tenantId,
  );
}

// ── 站点外壳（页头 / 页脚）───────────────────────────────────────────

/**
 * 外壳块与导航菜单。
 *
 * 页头页脚**不属于任何页面**，所以不走 `/v1/pages/:id/blocks`：
 * 一页一份页头意味着客户改一次要改四遍，还会漏一个。
 */
export function fetchSiteShell(tenantId: string, siteId: string): Promise<SiteShell> {
  return request<SiteShell>(`/v1/sites/${siteId}/shell`, {}, tenantId);
}

/** 只提交改动的那几块。props 走与页面区块同一条协议校验，服务端会拒不合法的数据 */
export function saveSiteShellBlocks(
  tenantId: string,
  siteId: string,
  blocks: SiteShellPatch[],
): Promise<SiteShell> {
  return request<SiteShell>(
    `/v1/sites/${siteId}/shell/blocks`,
    { method: 'PATCH', body: JSON.stringify({ blocks }) },
    tenantId,
  );
}

export function saveSiteNav(tenantId: string, siteId: string, nav: SiteNav): Promise<SiteShell> {
  return request<SiteShell>(
    `/v1/sites/${siteId}/shell/nav`,
    { method: 'PATCH', body: JSON.stringify(nav) },
    tenantId,
  );
}

/**
 * 外壳预览草稿（页头页脚页右侧的真实预览）。
 *
 * 与页面草稿同一个用途，但**按站点**存：外壳不属于任何页面，按页面存的话
 * 客户从别的页打开预览就看不到自己刚改的页头。只在预览态被站点读取。
 */
export function saveShellPreviewDraft(
  tenantId: string,
  siteId: string,
  input: { blocks: SiteShellBlock[]; nav: SiteNav },
): Promise<PreviewDraft> {
  return request<PreviewDraft>(
    `/v1/sites/${siteId}/shell/preview-draft`,
    { method: 'PUT', body: JSON.stringify(input) },
    tenantId,
  );
}

/** 丢掉外壳草稿：正式保存成功后、或预览在无改动状态下打开时调用 */
export function discardShellPreviewDraft(tenantId: string, siteId: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(
    `/v1/sites/${siteId}/shell/preview-draft`,
    { method: 'DELETE' },
    tenantId,
  );
}

// ── 商品目录 ──────────────────────────────────────────────────────────

export async function fetchCategories(tenantId: string, siteId: string): Promise<CategoryNode[]> {
  const body = await request<{ items: CategoryNode[] }>(
    `/v1/categories?siteId=${encodeURIComponent(siteId)}`,
    {},
    tenantId,
  );
  return body.items;
}

export function createCategory(
  tenantId: string,
  siteId: string,
  input: CategoryInput,
): Promise<CategoryNode> {
  return request<CategoryNode>(
    `/v1/categories?siteId=${encodeURIComponent(siteId)}`,
    { method: 'POST', body: JSON.stringify(input) },
    tenantId,
  );
}

/**
 * 同父级下重排分类（P1-07）。
 *
 * 提交这一层的完整 id 顺序，而不是"上移一位"。服务端会核对这批 id
 * 与库里现存的是否完全一致，不一致返回 409 stale_order —— 此时应当刷新列表，
 * 而不是把同一次请求重试一遍（重试必然还是不一致）。
 */
export function reorderCategories(
  tenantId: string,
  input: ReorderCategoriesInput,
): Promise<{ items: CategoryNode[] }> {
  return request<{ items: CategoryNode[] }>(
    '/v1/categories/reorder',
    { method: 'PUT', body: JSON.stringify(input) },
    tenantId,
  );
}

export function updateCategory(
  tenantId: string,
  id: string,
  patch: Partial<CategoryInput>,
): Promise<CategoryNode> {
  return request<CategoryNode>(
    `/v1/categories/${id}`,
    { method: 'PATCH', body: JSON.stringify(patch) },
    tenantId,
  );
}

export function deleteCategory(tenantId: string, id: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/v1/categories/${id}`, { method: 'DELETE' }, tenantId);
}

export interface ProductListParams {
  siteId: string;
  status?: ProductStatus;
  categoryId?: string;
  q?: string;
  page?: number;
  pageSize?: number;
}

export function fetchProducts(
  tenantId: string,
  params: ProductListParams,
): Promise<ProductListResult> {
  const query = new URLSearchParams({ siteId: params.siteId });
  if (params.status) query.set('status', params.status);
  if (params.categoryId) query.set('categoryId', params.categoryId);
  if (params.q) query.set('q', params.q);
  if (params.page) query.set('page', String(params.page));
  if (params.pageSize) query.set('pageSize', String(params.pageSize));
  return request<ProductListResult>(`/v1/products?${query.toString()}`, {}, tenantId);
}

export function fetchProduct(tenantId: string, id: string): Promise<ProductDetail> {
  return request<ProductDetail>(`/v1/products/${id}`, {}, tenantId);
}

export function createProduct(
  tenantId: string,
  siteId: string,
  input: ProductInput,
): Promise<ProductDetail> {
  return request<ProductDetail>(
    `/v1/products?siteId=${encodeURIComponent(siteId)}`,
    { method: 'POST', body: JSON.stringify(input) },
    tenantId,
  );
}

export function updateProduct(
  tenantId: string,
  id: string,
  patch: ProductInput,
): Promise<ProductDetail> {
  return request<ProductDetail>(
    `/v1/products/${id}`,
    { method: 'PATCH', body: JSON.stringify(patch) },
    tenantId,
  );
}

export function deleteProduct(tenantId: string, id: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/v1/products/${id}`, { method: 'DELETE' }, tenantId);
}

export function bulkProductStatus(
  tenantId: string,
  ids: string[],
  status: ProductStatus,
): Promise<{ updated: number }> {
  return request<{ updated: number }>(
    '/v1/products/bulk-status',
    { method: 'POST', body: JSON.stringify({ ids, status }) },
    tenantId,
  );
}

// ── 媒体库 ──────────────────────────────────────────────────────────

/**
 * 申请上传票据。文件本身不经过这个接口 —— 只有元数据。
 *
 * 返回 `reused: true` 表示同内容的图已经存在，直接用 `media.url`，
 * 不要再上传（客户把同一个 logo 传进五个页面是常态）。
 */
export function presignMedia(tenantId: string, input: PresignInput): Promise<PresignResult> {
  return request<PresignResult>(
    '/v1/media/presign',
    { method: 'POST', body: JSON.stringify(input) },
    tenantId,
  );
}

/** 上传完成后确认。服务端会用 HeadObject 核对真实大小与类型。 */
export function confirmMedia(
  tenantId: string,
  mediaId: string,
  body: ConfirmUploadInput = {},
): Promise<MediaItem> {
  return request<MediaItem>(
    `/v1/media/${mediaId}/confirm`,
    { method: 'POST', body: JSON.stringify(body) },
    tenantId,
  );
}

/**
 * 上传一张**程序生成**的图（品牌标）。
 *
 * 与 ImageUploader 走同一条链路，差别只有两处：
 *   · 字节来自 canvas，不是 <input type=file>
 *   · 宽高我们自己知道，不必再解码一次
 *
 * checksum 照算：同一个标点两次「用这个」不该在媒体库里存两份 ——
 * 品牌标是那个最容易被反复生成、反复覆盖的东西。
 */
export async function uploadImageBlob(
  tenantId: string,
  input: { siteId: string; blob: Blob; filename: string; width: number; height: number },
): Promise<MediaItem> {
  const checksum = await sha256OfBlob(input.blob);
  const presigned = await presignMedia(tenantId, {
    siteId: input.siteId,
    filename: input.filename,
    mime: 'image/png',
    bytes: input.blob.size,
    width: input.width,
    height: input.height,
    checksum,
  });
  if (presigned.reused) return presigned.media;

  const file = new File([input.blob], input.filename, { type: 'image/png' });
  await putToStorage(presigned.ticket, file);
  return confirmMedia(tenantId, presigned.ticket.mediaId, {
    width: input.width,
    height: input.height,
  });
}

/** http 环境下 crypto.subtle 不存在，此时退化为不去重（与 ImageUploader 同一套判断） */
async function sha256OfBlob(blob: Blob): Promise<string | null> {
  if (!globalThis.crypto?.subtle) return null;
  try {
    const digest = await globalThis.crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
    return Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return null;
  }
}

export function fetchMedia(
  tenantId: string,
  siteId: string,
  page = 1,
  pageSize = 24,
  q = '',
): Promise<{ items: MediaItem[]; total: number }> {
  const query = new URLSearchParams({
    siteId,
    page: String(page),
    pageSize: String(pageSize),
  });
  // 空关键字不带这个参数：服务端据此走"不加条件"的分支，
  // 传 `q=` 会多一次 trim 判断，也会让日志里全是空搜索
  if (q.trim()) query.set('q', q.trim());
  return request<{ items: MediaItem[]; total: number }>(
    `/v1/media?${query.toString()}`,
    {},
    tenantId,
  );
}

export function deleteMedia(tenantId: string, id: string): Promise<void> {
  return request<void>(`/v1/media/${id}`, { method: 'DELETE' }, tenantId);
}

/**
 * 重新生成派生图（缩略图）。
 *
 * 只在生成失败或卡住时可用；服务端对 ready / skipped 会回 409 ——
 * 不是"前端藏了按钮"，而是后端也不让做无意义的重复编码。
 */
export function regenerateMediaVariants(tenantId: string, id: string): Promise<MediaItem> {
  return request<MediaItem>(`/v1/media/${id}/variants`, { method: 'POST' }, tenantId);
}

// ── 主题市场 ──────────────────────────────────────────────────────────

// ── 主题发布（平台超管） ────────────────────────────────────────────
//
// 这几个调用**不带 tenantId**：它们操作的是平台级商品目录，
// 与"我现在带着哪个租户"没有关系。后端也不要求 x-tenant-id 头。

/** 只校验不落库。发布页用它做到"选完文件立刻知道问题在哪" */
export function validateThemePackage(manifest: unknown): Promise<ThemeValidationView> {
  return request<ThemeValidationView>('/v1/admin/themes/validate', {
    method: 'POST',
    body: JSON.stringify({ manifest }),
  });
}

export function publishTheme(
  manifest: unknown,
  preview?: { card?: string; hero?: string },
): Promise<ThemePublishResult> {
  return request<ThemePublishResult>('/v1/admin/themes', {
    method: 'POST',
    body: JSON.stringify({ manifest, preview }),
  });
}

/**
 * 申请一张预览图上传票据。
 *
 * 图不随 manifest 一起 POST：800KB 的图 base64 之后是 1.1MB，把整个 JSON
 * body 撑大又必须经过我们的进程，而这段体积本来可以不必走。服务端只签票，
 * 浏览器直传对象存储，传完把地址交给 `publishTheme`。
 */
export function presignThemePreview(
  kind: 'card' | 'hero',
  contentType: string,
): Promise<ThemePreviewTicket> {
  return request<ThemePreviewTicket>('/v1/admin/themes/preview', {
    method: 'POST',
    body: JSON.stringify({ kind, contentType }),
  });
}

/**
 * 把一张图直传对象存储，返回要提交给发布接口的地址。
 *
 * 这一跳**刻意不走 `request` 封装**：那个封装带会话头、带 20 秒超时、
 * 还会把非 2xx 统一翻成中文文案。而 COS 要的是票据里那几个精确的头
 * （少一个就是 SignatureDoesNotMatch），会话头带过去只会让签名对不上；
 * 失败原因也需要按"图没传上去"来讲，而不是"网络异常"。
 */
export async function uploadThemePreview(file: File, kind: 'card' | 'hero'): Promise<string> {
  const contentType = file.type || 'image/png';
  const ticket = await presignThemePreview(kind, contentType);

  let res: Response;
  try {
    res = await fetch(ticket.url, { method: 'PUT', headers: ticket.headers, body: file });
  } catch {
    // fetch 抛错只有一种含义：请求根本没发出去（断网、被拦截、CORS 预检失败）
    throw new Error('图没传上去：请求被中断或跨域被拦，换网络或重试一次');
  }
  if (!res.ok) {
    throw new Error(`图没传上去（对象存储返回 ${res.status}），重新选一次图再试`);
  }
  return ticket.publicUrl;
}

export function fetchThemePackages(): Promise<{ items: ThemePackageView[] }> {
  return request<{ items: ThemePackageView[] }>('/v1/admin/themes');
}

export function retireTheme(name: string): Promise<ThemePackageView> {
  return request<ThemePackageView>(`/v1/admin/themes/${encodeURIComponent(name)}`, {
    method: 'DELETE',
  });
}

export function restoreTheme(name: string): Promise<ThemePackageView> {
  return request<ThemePackageView>(`/v1/admin/themes/${encodeURIComponent(name)}/restore`, {
    method: 'POST',
  });
}

export function fetchThemeMarket(tenantId: string): Promise<ThemeMarketView> {
  return request<ThemeMarketView>('/v1/theme-market', {}, tenantId);
}

/**
 * 主题目录：**服务端注册表**里的全部主题摘要。
 *
 * 不能只信本地 `themeSummaries()`：控制台是静态包，它自带的 theme-kit 只有
 * 内置四套，后台发布的主题在本地根本不存在 —— 卡片没缩略图、应用面板没有
 * 变体可选，都源于这一点。摘要的解析与对比度计算留在服务端。
 */
export function fetchThemeCatalog(tenantId: string): Promise<{ items: ThemeSummary[] }> {
  return request<{ items: ThemeSummary[] }>('/v1/theme-market/catalog', {}, tenantId);
}

export function createThemeOrder(
  tenantId: string,
  themeName: string,
  provider?: 'wechat' | 'alipay',
): Promise<ThemeOrderView> {
  return request<ThemeOrderView>(
    '/v1/theme-market/orders',
    { method: 'POST', body: JSON.stringify({ themeName, ...(provider ? { provider } : {}) }) },
    tenantId,
  );
}

export function mockPayThemeOrder(tenantId: string, orderId: string): Promise<ThemeOrderView> {
  return request<ThemeOrderView>(
    `/v1/theme-market/orders/${orderId}/mock-pay`,
    { method: 'POST' },
    tenantId,
  );
}

/**
 * 退订 / 撤销退订。
 *
 * 服务端是**期末生效**：本期已付的钱不退，到期后自然失效。
 * 参数用 `cancel` 而不是两个接口 —— 撤销退订的路径和退订完全对称，
 * 拆成两个接口只会让前端多一个 if。
 */
export function setThemeCancel(
  tenantId: string,
  themeName: string,
  cancel: boolean,
): Promise<ThemeSubscriptionView> {
  return request<ThemeSubscriptionView>(
    `/v1/theme-market/subscriptions/${encodeURIComponent(themeName)}/cancel`,
    { method: 'POST', body: JSON.stringify({ cancel }) },
    tenantId,
  );
}

// ── 套餐与账单 ────────────────────────────────────────────────────────

export function fetchBilling(tenantId: string): Promise<BillingView> {
  return request<BillingView>('/v1/billing', {}, tenantId);
}

export function createBillingOrder(tenantId: string, input: CreateOrderInput): Promise<OrderView> {
  return request<OrderView>(
    '/v1/billing/orders',
    { method: 'POST', body: JSON.stringify(input) },
    tenantId,
  );
}

export function fetchBillingOrder(tenantId: string, orderId: string): Promise<OrderView> {
  return request<OrderView>(`/v1/billing/orders/${orderId}`, {}, tenantId);
}

export function cancelBillingOrder(
  tenantId: string,
  orderId: string,
): Promise<{ canceled: boolean }> {
  return request<{ canceled: boolean }>(
    `/v1/billing/orders/${orderId}/cancel`,
    { method: 'POST' },
    tenantId,
  );
}

/**
 * 模拟支付（仅当服务端开了 `ICETUS_BILLING_MOCK_PAY`）。
 *
 * 后端没开时这条接口回 404 —— 前端的 `mockPayEnabled` 只是"要不要显示按钮"，
 * 真正的门在服务端。所以这里不需要在客户端做任何判断。
 */
export function mockPayBillingOrder(tenantId: string, orderId: string): Promise<OrderView> {
  return request<OrderView>(`/v1/billing/orders/${orderId}/mock-pay`, { method: 'POST' }, tenantId);
}

/**
 * 降档影响预览。
 *
 * 单独一个 GET：客户点「降为这一档」之后要先看到清单再决定，
 * 那时什么都还没提交，不该产生任何写入。
 */
export function fetchDowngradePreview(
  tenantId: string,
  planCode: PlanCode,
): Promise<DowngradePreview> {
  return request<DowngradePreview>(`/v1/billing/downgrade/${planCode}`, {}, tenantId);
}

/**
 * 申请降档（到期生效）。传 null 撤销申请。
 *
 * `choices` 是客户当场选好的处理方案，**降档时必须带**：到期那天扫描器
 * 只照单执行，没有选择就会被服务端拒掉（409）。撤销时不用带。
 */
export function changePlan(
  tenantId: string,
  planCode: PlanCode | null,
  choices?: DowngradeChoices,
): Promise<{ pendingPlanCode: PlanCode | null }> {
  return request<{ pendingPlanCode: PlanCode | null }>(
    '/v1/billing/plan',
    { method: 'PATCH', body: JSON.stringify(choices ? { planCode, choices } : { planCode }) },
    tenantId,
  );
}

// ── 成员与邀请（P1-04）─────────────────────────────────────────────

export function fetchMembers(tenantId: string): Promise<MembersView> {
  return request<MembersView>('/v1/members', {}, tenantId);
}

export function inviteMember(
  tenantId: string,
  input: { email: string; role: Role },
): Promise<{
  link: string;
  mailed: boolean;
  invitation: { id: string; email: string; role: Role };
}> {
  return request(
    '/v1/members/invitations',
    { method: 'POST', body: JSON.stringify(input) },
    tenantId,
  );
}

export function revokeInvitation(tenantId: string, id: string): Promise<{ revoked: boolean }> {
  return request<{ revoked: boolean }>(
    `/v1/members/invitations/${id}`,
    { method: 'DELETE' },
    tenantId,
  );
}

export function changeMemberRole(
  tenantId: string,
  userId: string,
  role: Role,
): Promise<{ userId: string; role: Role }> {
  return request(
    `/v1/members/${userId}`,
    { method: 'PATCH', body: JSON.stringify({ role }) },
    tenantId,
  );
}

export function removeMember(tenantId: string, userId: string): Promise<{ removed: boolean }> {
  return request(`/v1/members/${userId}`, { method: 'DELETE' }, tenantId);
}

/**
 * 邀请链接的预览与接受。
 *
 * 这两条**不带 x-tenant-id**：打开链接的人还不知道自己属于哪个租户，
 * 令牌本身就是全部上下文。带上一个猜的租户 ID 只会让请求 403。
 */
export function previewInvitation(token: string): Promise<InvitationPreview> {
  return request<InvitationPreview>(`/v1/invitations/${encodeURIComponent(token)}`);
}

export function acceptInvitation(
  token: string,
): Promise<{ tenantId: string; tenantName: string; role: Role; joined: boolean }> {
  return request(`/v1/invitations/${encodeURIComponent(token)}/accept`, { method: 'POST' });
}

/**
 * 在邀请链接上直接建账号（收件人原本没有账号时）。
 *
 * **不建站**：他加入的是一家已经有站的公司。邮箱由邀请决定，这里不传 ——
 * 传了服务端也不看，那是为了让"谁拿到链接都能给任意邮箱建号"这件事在接口
 * 形状上就不可能发生。
 */
export function signupFromInvitation(
  token: string,
  password: string,
): Promise<{ email: string; verificationTtlHours: number }> {
  return request(`/v1/invitations/${encodeURIComponent(token)}/signup`, {
    method: 'POST',
    body: JSON.stringify({ password, acceptedTerms: true }),
  });
}

/**
 * 操作记录（P1-05）。
 *
 * 游标分页：offset 在"客户一边翻页一边有人操作"时会重复或跳过记录，
 * 而漏一条正好是审计最不能出的错。所以游标由服务端给，前端只管原样回传。
 */
export function fetchAudit(
  tenantId: string,
  query: { kind?: string | null; cursor?: string | null } = {},
): Promise<AuditListView> {
  const params = new URLSearchParams();
  if (query.kind) params.set('kind', query.kind);
  if (query.cursor) params.set('cursor', query.cursor);
  const qs = params.toString();
  return request<AuditListView>(`/v1/audit${qs ? `?${qs}` : ''}`, {}, tenantId);
}

// ── 多语言翻译 ────────────────────────────────────────────────────────

export function fetchTranslationOverview(
  tenantId: string,
  siteId: string,
): Promise<TranslationOverview> {
  return request(`/v1/translation/overview?siteId=${encodeURIComponent(siteId)}`, {}, tenantId);
}

export function estimateTranslation(
  tenantId: string,
  input: { siteId: string; targetLocales: string[] } & TranslationScope,
): Promise<TranslationEstimate[]> {
  return request(
    '/v1/translation/estimate',
    { method: 'POST', body: JSON.stringify(input) },
    tenantId,
  );
}

export function createTranslationJob(
  tenantId: string,
  input: { siteId: string; targetLocale: string } & TranslationScope,
): Promise<TranslationJobSummary> {
  return request('/v1/translation/jobs', { method: 'POST', body: JSON.stringify(input) }, tenantId);
}

export function fetchTranslationJob(tenantId: string, id: string): Promise<TranslationJobSummary> {
  return request(`/v1/translation/jobs/${id}`, {}, tenantId);
}

/**
 * 编辑页里的「顺手翻一下」：把表单里这一份内容翻成目标语言，结果直接返回。
 *
 * 与 `createTranslationJob` 的区别：不建任务、不写库、不同步到目标语言页面 ——
 * 它只负责把文字翻好给前端填进输入框，客户看过再自己保存。
 */
export function translateInline(
  tenantId: string,
  input: InlineTranslationInput,
): Promise<InlineTranslationResult> {
  return request(
    '/v1/translation/inline',
    { method: 'POST', body: JSON.stringify(input) },
    tenantId,
  );
}

export function cancelTranslationJob(tenantId: string, id: string): Promise<{ ok: true }> {
  return request(`/v1/translation/jobs/${id}/cancel`, { method: 'POST' }, tenantId);
}

export function fetchTranslationUnits(
  tenantId: string,
  params: { siteId: string; locale: string; filter?: 'all' | 'review' | 'human' },
): Promise<TranslationUnitView[]> {
  const query = new URLSearchParams({ siteId: params.siteId, locale: params.locale });
  if (params.filter) query.set('filter', params.filter);
  return request(`/v1/translation/units?${query.toString()}`, {}, tenantId);
}

export function patchTranslationUnit(
  tenantId: string,
  memoryId: string,
  target: string,
): Promise<{ ok: true }> {
  return request(
    `/v1/translation/units/${memoryId}`,
    { method: 'PATCH', body: JSON.stringify({ target }) },
    tenantId,
  );
}

/** 还原为机器译文（需要这条被人工改过、且保留着机器快照） */
export function revertTranslationUnit(tenantId: string, memoryId: string): Promise<{ ok: true }> {
  return request(`/v1/translation/units/${memoryId}/revert`, { method: 'POST' }, tenantId);
}

export function publishTranslation(
  tenantId: string,
  siteId: string,
  locale: string,
): Promise<{ published: number }> {
  return request(
    '/v1/translation/publish',
    { method: 'POST', body: JSON.stringify({ siteId, locale }) },
    tenantId,
  );
}

export function fetchTranslationGlossary(
  tenantId: string,
  siteId: string,
  locale?: string,
): Promise<TranslationGlossaryEntry[]> {
  const query = new URLSearchParams({ siteId });
  if (locale) query.set('locale', locale);
  return request(`/v1/translation/glossary?${query.toString()}`, {}, tenantId);
}

export function saveTranslationGlossary(
  tenantId: string,
  input: {
    siteId: string;
    locale: string;
    entries: { sourceTerm: string; targetTerm: string; note?: string | null }[];
  },
): Promise<TranslationGlossaryEntry[]> {
  return request(
    '/v1/translation/glossary',
    { method: 'PUT', body: JSON.stringify(input) },
    tenantId,
  );
}

export function fetchShellTranslation(
  tenantId: string,
  siteId: string,
  locale: string,
): Promise<{
  blocks: { instanceId: string; props: Record<string, unknown> }[];
  nav: SiteNav | null;
}> {
  const query = new URLSearchParams({ siteId, locale });
  return request(`/v1/translation/shell?${query.toString()}`, {}, tenantId);
}

export function saveShellTranslation(
  tenantId: string,
  input: {
    siteId: string;
    locale: string;
    blocks: { instanceId: string; props: Record<string, unknown> }[];
    nav?: SiteNav | null;
  },
): Promise<{ ok: true }> {
  return request('/v1/translation/shell', { method: 'PUT', body: JSON.stringify(input) }, tenantId);
}

export function deleteShellTranslation(
  tenantId: string,
  siteId: string,
  locale: string,
): Promise<{ ok: true }> {
  const query = new URLSearchParams({ siteId, locale });
  return request(`/v1/translation/shell?${query.toString()}`, { method: 'DELETE' }, tenantId);
}

/** 平台级：只有超管能调（后端按 users.is_platform_admin 判定） */
export function fetchTranslationProvider(): Promise<TranslationProviderView> {
  return request('/v1/admin/translation/provider');
}

export function saveTranslationProvider(
  input: UpdateTranslationProviderInput,
): Promise<TranslationProviderView> {
  return request('/v1/admin/translation/provider', { method: 'PUT', body: JSON.stringify(input) });
}

export function testTranslationProvider(): Promise<{
  ok: boolean;
  message: string;
  latencyMs: number;
}> {
  return request('/v1/admin/translation/provider/test', { method: 'POST' });
}

// ── 品牌 AI 图形 ────────────────────────────────────────────────────

/**
 * AI 生成能不能用、这个月还剩几张。
 *
 * 客户不该知道我们用哪家模型、Key 是什么；他需要知道"现在能不能点"、
 * 不能点的时候一句原因、以及额度还剩几张 —— 后者会直接改变他的行为。
 */
export function fetchBrandArtAvailability(tenantId: string): Promise<BrandArtAvailability> {
  return request('/v1/brand/art/availability', {}, tenantId);
}

export interface CreateArtJobInput {
  siteId: string;
  /**
   * 画法。两套 key 不重叠，服务端按 `mode` 选校验器：
   * 标的 geometric/industrial… 和封面的 scene/product/macro/abstract。
   */
  style: BrandArtStyle | CoverArtStyle;
  /** 'full' 连文字一起画；'symbol' 只要图形；'cover' 照片类配图（分类卡/首屏图/横幅） */
  mode: BrandArtMode;
  /** cover 模式画什么（客户原文）。标模式不用传 */
  subject?: string | null;
  /**
   * cover 模式的画布比例（3:2 / 4:3 / 1:1 / 21:9 / 3:1 / 16:10）。
   *
   * 由两个地方决定：分类封面恒为 `cover`，区块里的图看字段描述符的 `artAspect`。
   * 不传就是 3:2 —— 服务端按分类卡兜底。
   */
  aspect?: ArtAspect;
  /** full 模式下要模型写出来的名字 */
  wordmark?: string | null;
  prompt: string | null;
  color?: string | null;
  /**
   * 站点页头是深色的。
   *
   * 不让服务端自己猜：页头颜色是主题给前端渲染的，服务端手上没有；
   * 而且客户可能换主题，同一张标要按"它将来压在哪"来画。
   */
  dark?: boolean;
}

export function createBrandArtJob(
  tenantId: string,
  input: CreateArtJobInput,
): Promise<BrandArtJob> {
  return request<BrandArtJob>(
    '/v1/brand/art/jobs',
    { method: 'POST', body: JSON.stringify(input) },
    tenantId,
  );
}

export function fetchBrandArtJob(tenantId: string, id: string): Promise<BrandArtJob> {
  return request<BrandArtJob>(`/v1/brand/art/jobs/${id}`, {}, tenantId);
}

export function fetchBrandImageConfig(): Promise<BrandImageConfigView> {
  return request('/v1/admin/brand/config');
}

export function saveBrandImageConfig(
  input: UpdateBrandImageConfigInput,
): Promise<BrandImageConfigView> {
  return request('/v1/admin/brand/config', { method: 'PUT', body: JSON.stringify(input) });
}

export function testBrandImageConfig(): Promise<BrandImageTestResult> {
  return request('/v1/admin/brand/config/test', { method: 'POST' });
}
